"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, isValidElement, useId, useState, } from "react";
import { cn } from "../utils/cn.js";
// physical: intentional — `side` is an explicit caller-chosen physical
// placement, and the top/bottom rows use `left-1/2 -translate-x-1/2` purely
// to CENTER horizontally (symmetric: stays centered in both LTR and RTL).
// Swapping to logical here would desync from the non-logical -translate-x.
const SIDE = {
    top: "left-1/2 -translate-x-1/2 bottom-[calc(100%+8px)]",
    bottom: "left-1/2 -translate-x-1/2 top-[calc(100%+8px)]",
    left: "top-1/2 -translate-y-1/2 right-[calc(100%+8px)]",
    right: "top-1/2 -translate-y-1/2 left-[calc(100%+8px)]",
};
/**
 * Tooltip — keyboard-reachable hint that surfaces on hover OR focus.
 * 400ms delay-in / 100ms delay-out matches the brief §15 contract.
 * Applies `aria-describedby` directly to the trigger child via
 * cloneElement (the outer span is purely positioning context), so AT
 * announces the description on the actual focusable element.
 *
 * Re-exported as `InfoHint` from the package barrel (Q3 carry-forward).
 */
export function Tooltip({ content, side = "top", children, className, }) {
    const [open, setOpen] = useState(false);
    const id = useId();
    if (!isValidElement(children)) {
        throw new Error("Tooltip expects a single React element child so aria-describedby can be applied to the focusable trigger.");
    }
    // Preserve any pre-existing aria-describedby on the child so we don't
    // clobber a description the consumer already wired (e.g. a form-error
    // hint). Combine via space-separated id list per ARIA spec.
    const existingDescribedBy = children.props["aria-describedby"];
    const describedBy = open
        ? [existingDescribedBy, id].filter(Boolean).join(" ")
        : existingDescribedBy;
    const trigger = cloneElement(children, { "aria-describedby": describedBy });
    return (_jsxs("span", { "data-slot": "tooltip-root", className: cn("relative inline-flex", className), onMouseEnter: () => setOpen(true), onMouseLeave: () => setOpen(false), onFocus: () => setOpen(true), onBlur: () => setOpen(false), children: [trigger, _jsx("span", { id: id, role: "tooltip", "aria-hidden": !open, "data-open": open ? "true" : "false", "data-side": side, className: cn("pointer-events-none absolute z-40 whitespace-nowrap rounded-sm bg-ink-900 px-2 py-1 text-caption text-paper-warm shadow-2", "transition-opacity ease-out", open
                    ? "opacity-100 duration-fast [transition-delay:400ms]"
                    : "opacity-0 duration-fast [transition-delay:100ms]", SIDE[side]), children: content })] }));
}
